import React, { useState, useEffect, useCallback } from 'react';
import {
    Users, TrendingUp, Zap, Activity, ShieldCheck,
    AlertTriangle, Bug, Lightbulb, Star, MessageSquare,
    Loader2,
} from 'lucide-react';
import { adminAnalytics } from '../../services/api';
import MetricCard from './MetricCard';
import MetricDrawer from './MetricDrawer';
import DecisionSummaryBanner from './DecisionSummaryBanner';

/**
 * OverviewTab — Clickable metric cards for all overview data.
 *
 * Props:
 *   filters: { days, compare, district_id, campus_id }
 */

const OverviewTab = ({ filters = {} }) => {
    const [overview, setOverview] = useState(null);
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [drawerMetric, setDrawerMetric] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                days: filters.days || 7,
                ...(filters.district_id && { district_id: filters.district_id }),
                ...(filters.campus_id && { campus_id: filters.campus_id }),
            };
            const [overviewRes, healthRes] = await Promise.all([
                adminAnalytics.getOverview(params),
                adminAnalytics.getHealth(params),
            ]);
            setOverview(overviewRes.data);
            setHealth(healthRes.data);
        } catch (err) {
            if (err.response?.status === 403) {
                setError('Access denied. Admin role required.');
            } else {
                setError(err.response?.data?.detail || 'Failed to load overview data.');
            }
        } finally {
            setLoading(false);
        }
    }, [filters.days, filters.district_id, filters.campus_id]);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
                <span style={{ fontSize: 13, color: '#6b7280' }}>Loading overview…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                <p style={{ fontSize: 14, color: '#ef4444', fontWeight: 500, margin: 0 }}>{error}</p>
                <button onClick={loadData} style={{ fontSize: 13, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer' }}>
                    Retry
                </button>
            </div>
        );
    }

    const users = overview?.users || {};
    const engagement = overview?.engagement || {};
    const healthData = overview?.health || {};
    const feedback = overview?.feedback || {};
    const pipeline = health?.pipeline || {};
    const systemEvents = health?.system_events || {};

    const successRate = pipeline.success_rate_pct ?? healthData.success_rate_pct ?? 100;
    const successColor = successRate >= 99 ? '#10b981' : successRate >= 95 ? '#f59e0b' : '#ef4444';

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>Overview</h2>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
                    Last {filters.days || 7} days
                </p>
            </div>

            {/* Decision Summary Banner — period-over-period signals */}
            <DecisionSummaryBanner filters={filters} />

            {/* Users */}
            <Section title="Users" icon={Users} color="#3b82f6">
                <MetricCard label="Total Registered" value={users.total_registered ?? 0} icon={Users} accent="#3b82f6" metricKey="active_users" onClick={setDrawerMetric} />
                <MetricCard label={`Active (${filters.days || 7}d)`} value={users.active_last_n_days ?? 0} icon={TrendingUp} accent="#3b82f6" sub={users.active_percentage != null ? `${users.active_percentage}% of total` : null} metricKey="active_users" onClick={setDrawerMetric} />
            </Section>

            {/* Engagement */}
            <Section title="Engagement" icon={Zap} color="#10b981">
                <MetricCard label="Lessons Generated" value={engagement.lessons_generated ?? 0} icon={Zap} accent="#10b981" metricKey="lessons_generated" onClick={setDrawerMetric} />
                <MetricCard label="Chat Messages" value={engagement.chat_messages ?? 0} icon={MessageSquare} accent="#10b981" metricKey="chat_messages" onClick={setDrawerMetric} />
                <MetricCard label="Total Requests" value={engagement.total_requests ?? 0} icon={Activity} accent="#10b981" metricKey="total_requests" onClick={setDrawerMetric} />
            </Section>

            {/* Health */}
            <Section title="System Health" icon={ShieldCheck} color={successColor}>
                <MetricCard label="Success Rate" value={successRate} icon={ShieldCheck} accent={successColor} format="percent" metricKey="success_rate" onClick={setDrawerMetric} />
                <MetricCard label="Failures" value={pipeline.failures ?? healthData.failures ?? 0} icon={AlertTriangle} accent={pipeline.failures > 0 ? '#ef4444' : '#10b981'} metricKey="failures" onClick={setDrawerMetric} />
                <MetricCard label="Error Rate" value={pipeline.error_rate_pct != null ? pipeline.error_rate_pct : '—'} icon={Bug} accent="#f59e0b" format="percent" metricKey="error_rate" onClick={setDrawerMetric} />
                <MetricCard label="System Events" value={systemEvents.total_error_events ?? 0} icon={Activity} accent="#8b5cf6" sub={systemEvents.breakdown?.length ? `${systemEvents.breakdown.length} event types` : null} metricKey="system_events" onClick={setDrawerMetric} />
            </Section>

            {/* Feedback */}
            <Section title="Open Feedback" icon={Lightbulb} color="#f59e0b">
                <MetricCard label="Open Bugs" value={feedback.open_bugs ?? 0} icon={Bug} accent="#ef4444" metricKey="open_bugs" onClick={setDrawerMetric} />
                <MetricCard label="Improvements" value={feedback.open_improvements ?? 0} icon={Lightbulb} accent="#f59e0b" />
                <MetricCard label="Feature Requests" value={feedback.open_feature_requests ?? 0} icon={Star} accent="#8b5cf6" />
            </Section>

            {/* Metric Drawer */}
            {drawerMetric && (
                <MetricDrawer
                    metricKey={drawerMetric}
                    filters={filters}
                    onClose={() => setDrawerMetric(null)}
                />
            )}

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};


const Section = ({ title, icon: Icon, color = '#6366f1', children }) => (
    <div style={{ marginBottom: 28 }}>
        <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            marginBottom: 12, paddingBottom: 8,
            borderBottom: `2px solid ${color}20`,
        }}>
            {Icon && <Icon size={15} style={{ color }} />}
            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>{title}</h3>
        </div>
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
            gap: 12,
        }}>
            {children}
        </div>
    </div>
);


export default OverviewTab;
